import { Component, Input, OnInit, HostBinding } from '@angular/core';
import { map, tap, startWith } from 'rxjs/operators';
import { getChartOptions, colors } from '../../../redux/models/dashboard/chart-options';
import { State as ReportState } from '../../../redux/reducers/dashboard/reducers-report';
import { CombinedState as DashboardCombinedState } from '../../../redux/reducers/dashboard/reducers-dashboard';
import { DashboardService } from '../../../redux/services/dashboard/dashboard.service';
import { PdfService } from '../../../redux/services/dashboard/pdf.service';
import * as chartJs from 'chart.js';
import { getFilterBySymbol, camelize } from '../../../redux/services/utils/data-helpers';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '../../../redux/reducers';
import { Update } from '../../../redux/actions/dashboard/actions-report';
import { turnoverOptions } from '../../../redux/models/dashboard/option-values';

@Component({
  standalone: false,
  selector: 'app-dashboard-main',
  templateUrl: './dashboard-main.component.html',
  styleUrls: ['./dashboard-main.component.scss']
})
export class DashboardMainComponent implements OnInit {
  @Input() tile = false;

  legendItems: { text: string; color: string }[];
  dashboardState$: Observable<DashboardCombinedState>;
  chartData$: Observable<any>;
  isLoading$: Observable<boolean>;
  chart: chartJs;
  updateTimeout: number;
  showLegend = false;
  title = '';
  dashboardText = '';
  dashboardNoDataForPeriodString = $localize`:@@dashboard-main.dashboardNoDataForPeriod:No data for the specified period`;
  pageSomethingwentwrongHeadingString = $localize`:@@dashboard-main.pageSomethingwentwrongHeading:Oops... Something went wrong`;
  dashboardNoDataForDefaultPeriodString = $localize`:@@dashboard-main.dashboardNoDataForDefaultPeriod:You did not book any tickets for the next 6 months`;
  private chartOptions: any;

  constructor(
    private readonly store: Store<State>,
    private readonly dashboardService: DashboardService,
    private readonly pdf: PdfService
  ) {}

  @HostBinding()
  get class() {
    return 'dashboard-main-chart' + (this.tile ? ' chart-tile fix-height' : '');
  }

  ngOnInit() {
    this.store.dispatch(new Update());
    this.dashboardState$ = this.dashboardService.getNewLoadedDashboard();
    this.chartData$ = this.dashboardState$.pipe(
      tap(this.updateFromState.bind(this)),
      map(dashboardState => this.mapReportData(dashboardState.report))
    );

    this.isLoading$ = this.dashboardService.getDashboard().pipe(
      map(dashboardState => dashboardState.report.updateRequired),
      startWith(true)
    );
  }

  private updateFromState(dashboardState: DashboardCombinedState) {
    const filterBySymbol = getFilterBySymbol(dashboardState);
    this.chartOptions = getChartOptions(filterBySymbol, this.tile);
    const turnover = dashboardState.options.filters.turnover;
    this.showLegend = ['CAR', 'HOTEL'].indexOf(turnover) === -1;
    const turnoverOption = turnoverOptions.find(v => v.value === turnover);
    this.title = turnoverOption ? turnoverOption.label : '';
  }

  private mapReportData(reportState: ReportState) {
    if (reportState.errorMessage) {
      return null;
    }
    return this.prepareDatasets(reportState);
  }

  private prepareDatasets(reportState: ReportState) {
    const { labels, data } = this.prepareDataSet(reportState);
    const { datasets, newLegendItems } = this.filterEmptyDatasets(data);

    const chartConfig: chartJs.ChartConfiguration = {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: this.chartOptions
    };
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    this.updateTimeout = window.setTimeout(() => {
      if (datasets.length) {
        this.legendItems = newLegendItems;
        if (this.chart) {
          this.chart.destroy();
        }
        this.chart = new chartJs.Chart('dashboard-main-chart-ctx', chartConfig);
        this.pdf.setMainChartRef(this.chart);
      }
    }, 0);

    return {
      labels,
      datasets
    };
  }

  private prepareDataSet(reportState: ReportState) {
    const labels: string[] = [];
    const data: { [key: string]: number[] } = {};

    // prepare data for datasets: { datasetType: [values for all labels] }
    reportState.data.bookingReport.bookings.forEach(booking => {
      if (
        // skip zero values for tile version
        !this.tile ||
        Object.entries(booking.values).reduce(
          (acc, [_ /*eslint-disable-line @typescript-eslint/no-unused-vars*/, v]) => acc + v,
          0
        )
      ) {
        labels.push(booking.date);
        Object.entries(booking.values).forEach(([key, value]) => {
          if (typeof data[key] !== 'undefined') {
            data[key].push(value);
          } else {
            data[key] = [value];
          }
        });
      }
    });

    return {
      labels,
      data
    };
  }

  private filterEmptyDatasets(data: { [key: string]: number[] } | undefined[]) {
    const datasets = [];
    const newLegendItems = [];

    // filter out empty datasets and form legend items
    const tempData: { [key: string]: number[] } | undefined[] = data ? data : [];
    Object.entries(tempData).forEach(([key, value]) => {
      if (!value.reduce((prev, next) => prev + next)) {
        return; // there's no data in the dataset
      }
      datasets.push({
        label: key,
        data: value,
        backgroundColor: colors[key],
        hoverBackgroundColor: colors[key]
      });

      switch (camelize(key)) {
        case 'Completed':
          this.dashboardText = $localize`:@@csv.service.dashboardCompleted:Completed`;
          break;
        case 'Ticketed':
          this.dashboardText = $localize`:@@csv.service.dashboardTicketed:Ticketed`;
          break;
        case 'Unticketed':
          this.dashboardText = $localize`:@@csv.service.dashboardUnticketed:Unticketed`;
          break;
        default:
          this.dashboardText = '';
          break;
      }

      newLegendItems.push({
        text: this.dashboardText,
        color: colors[key]
      });
    });

    return {
      datasets,
      newLegendItems
    };
  }
}
